<script setup>
import ListFetcher from '@/features/data/presentation/components/ListFetcher.vue'
import PaginationUi from './PaginationUi.vue'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import { computed, ref, watch } from 'vue'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import InputComponent from '@/presentation/components/input/InputComponent.vue'

const search = ref('')
const sortKey = ref('none')
const sortOrder = ref('desc') // 'asc' ou 'desc'
const colFilters = ref({})
const hasSelectedCol = computed(() => {
  return Object.values(colFilters.value).some(el => !!el)
})

const fetcherRef = ref(null)
watch([search, sortKey, sortOrder, colFilters], () => {
  if (fetcherRef.value && typeof fetcherRef.value.changePage === 'function') {
    fetcherRef.value.changePage(0)
  }
}, { deep: true })
defineProps({
  title: String,
  dataKey: { type: String, required: true },
  hasSearch: { type: Boolean, default: false },
  hidePagination: { type: Boolean, default: false },
  buttons: {
    type: Array,
  },
  cols: {
    type: Array,
    required: true,
  },
  refreshOnEvents: {
    type: [String, Array],
    default: () => [],
  },
})


function getInitials(str) {
  if (typeof str !== 'string') return str
  if (!str) return ''
  const words = str.trim().split(/\s+/)
  if (words.length === 1) {
    const word = words[0]
    if (word.length === 1) return word[0].toUpperCase()
    return (word[0] + word[word.length - 1]).toUpperCase()
  }
  return words
    .map(w => w[0].toUpperCase())
    .join('')
    .slice(0, 2)
}

function handleSort(col) {
  if (!col.sortKey) return
  if (sortKey.value === col.sortKey) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = col.sortKey
    sortOrder.value = 'asc'
  }
}

function handleActionClick(col, el, option) {
  // Ne pas appeler si option est null/undefined ou vide
  if (!option || (typeof option === 'object' && Object.keys(option).length === 0)) {
    return
  }

  if (col && typeof col.onClick === 'function') {
    col.onClick(option, el)
  }
}
const emit = defineEmits(['row-click', 'fetched'])

function handleRowClick(el) {
  emit('row-click', el)
}
</script>

<template>
  <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white pt-4">
    <!-- HEADER: titre + recherche -->
    <div
      class="flex flex-col gap-5 px-6 mb-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h3 class="text-xl font-semibold text-gray-800">{{ title }}</h3>
      </div>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          v-for="(button, i) in buttons"
          :key="i"
          @click="button.onClick"
          class="buttonComponent whitespace-nowrap"
        >
          <span v-html="button.title"></span>
        </button>
        <div class="relative" v-if="hasSearch">
          <button class="absolute -translate-y-1/2 left-4 top-1/2">
            <svg
              class="fill-gray-500"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M3.04199 9.37381C3.04199 5.87712 5.87735 3.04218 9.37533 3.04218C12.8733 3.04218 15.7087 5.87712 15.7087 9.37381C15.7087 12.8705 12.8733 15.7055 9.37533 15.7055C5.87735 15.7055 3.04199 12.8705 3.04199 9.37381ZM9.37533 1.54218C5.04926 1.54218 1.54199 5.04835 1.54199 9.37381C1.54199 13.6993 5.04926 17.2055 9.37533 17.2055C11.2676 17.2055 13.0032 16.5346 14.3572 15.4178L17.1773 18.2381C17.4702 18.531 17.945 18.5311 18.2379 18.2382C18.5308 17.9453 18.5309 17.4704 18.238 17.1775L15.4182 14.3575C16.5367 13.0035 17.2087 11.2671 17.2087 9.37381C17.2087 5.04835 13.7014 1.54218 9.37533 1.54218Z"
              />
            </svg>
          </button>
          <input
            type="text"
            v-model="search"
            placeholder="Rechercher..."
            class="h-11 w-full rounded-lg border border-gray-300 bg-transparent py-2.5 pl-[42px] pr-4 text-base text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-primary-300 focus:outline-hidden focus:ring-3 focus:ring-primary-500/10 xl:w-[300px]"
          />
        </div>
      </div>
    </div>

    <!-- LIST FETCHER + TABLE -->
    <div class="min-w-full overflow-x-auto custom-scrollbar">
      <ListFetcher
        ref="fetcherRef"
        :dataKey="dataKey"
        :refreshOnEvents="refreshOnEvents"
        :filters="{ search, sortKey, sortOrder, ...colFilters }"
        @fetched="$emit('fetched', $event)"
      >

        <template #loadingAndItems="{ items, limit, loading, result }">
          <div class="overflow-auto w-full">
            <table
              class="min-w-full w-max overflow-auto"
              :class="{ shimmerContainer: loading }"
            >
              <thead>
                <tr class="border-t border-gray-100 bg-gray-50">
                  <th
                    class="px-6 py-3 text-left"
                    v-for="(col, i) in cols"
                    :key="i"
                  >
                    <div class="flex items-center gap-1 text-gray-500 h-full">
                      <div
                        class="flex gap-2 items-center cursor-pointer ripple rounded"
                        :class="{
                          underline: sortKey === col.sortKey,
                        }"
                        @click="handleSort(col)"
                      >
                        <span class="font-medium text-sm">
                          {{ col.name }}
                        </span>

                        <svg
                          v-if="col.sortKey"
                          xmlns="http://www.w3.org/2000/svg"
                          class="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 15l7-7 7 7"
                            v-show="
                              sortKey !== col.sortKey || sortOrder === 'asc'
                            "
                          />
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                            v-show="
                              sortKey === col.sortKey && sortOrder === 'desc'
                            "
                          />
                        </svg>
                      </div>

                      <div
                        v-if="
                          col.filter?.key && Array.isArray(col.filter?.options)
                        "
                      >
                        <InputComponent
                          type="select"
                          :options="col.filter?.options"
                          :formatOptionsDisplay="
                            el => {
                              let value = `<div class='flex justify-between items-center gap-5'> <span>${el?.name ?? '-'}</span>  `
                              if (colFilters[col.filter.key] == el.value)
                                value += `<div class='w-2 h-2 bg-primary rounded-full'></div>`
                              value += `</div>`
                              return value
                            }
                          "
                          @input="
                            el => {
                              colFilters[col.filter.key] = el.value
                            }
                          "
                          v-slot="{ data: slotData } = { data: null }"
                        >
                          <div
                            class="p-1 rounded ripple cursor-pointer"
                            @mouseover="
                              () => {
                                slotData?.show()
                              }
                            "
                          >
                            <ColorImg
                              src="/images/icons/filter.svg"
                              class="bg-current w-4 h-4"
                            />
                            <div
                              class="absolute rounded-full w-2 h-2 bg-primary top-0 right-0"
                              v-if="!!colFilters[col.filter.key]"
                            ></div>
                          </div>
                        </InputComponent>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  class="border-t border-gray-100 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  v-for="(el, i) in [
                    ...(items || []),
                    ...(loading
                      ? [
                          ...items,
                          ...items,
                          ...items,
                          ...items,
                          ...items,
                          ...items,
                          ...items,
                          ...items,
                          ...items,
                          ...items,
                        ]
                      : []),
                    ...(loading ? Array.from({ length: 10 }, () => ({})) : []),
                  ].slice(0, loading ? limit : 10000)"
                  :key="i"
                  @click="handleRowClick(el)"
                >
                  <td
                    class="px-6 py-4 whitespace-nowrap"
                    v-for="(col, j) in cols"
                    :key="j"
                  >
                    <div
                      v-if="j === 0 && Array.isArray(col.value(el))"
                      class="flex items-center gap-3"
                    >
                      <div
                        class="flex items-center justify-center w-10 h-10 rounded-full overflow-hidden border border-primary-50 bg-primary-100 shimmerTarget"
                      >
                        <div
                          class="w-full h-full"
                          v-if="col.image && typeof col.image == 'function'"
                        >
                          <ImgComponent
                            class="w-full h-full object-cover bg-white"
                            fallback="/images/logo.png"
                            :src="col.image(el)"
                          />
                        </div>
                        <span
                          class="text-xs font-semibold text-primary-500"
                          v-else
                        >
                          {{ getInitials(col.value(el)[0]) }}
                        </span>
                      </div>
                      <div>
                        <span
                          class="mb-0.5 block text-sm font-medium text-gray-700 shimmerTarget"
                        >
                          <span v-html="col.value(el)[0]"></span>
                        </span>
                        <span
                          v-if="col.value(el).length === 2"
                          class="text-gray-500 text-sm shimmerTarget"
                        >
                          <span v-html="col.value(el)[1]"></span>
                        </span>
                      </div>
                    </div>
                    <span
                      v-else-if="col.id === 'action'"
                      class="text-gray-700 text-sm flex justify-end shimmerTarget"
                    >
                      <button
                        v-if="
                          typeof col.options == 'function' &&
                          typeof col.icon == 'function'
                        "
                      >
                        <InputComponent
                          type="select"
                          :options="col.options(el)"
                          :formatOptionsDisplay="opt => opt?.name ?? '-'"
                          @input="option => handleActionClick(col, el, option)"
                        >
                          <div class="p-2 cursor-pointer ripple relative">
                            <ColorImg
                              :src="col.icon(el)"
                              class="bg-current w-6 min-w-6 h-6"
                              alt=""
                            />
                            <span
                              v-if="col.badge && col.badge(el) > 0"
                              class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white shadow-sm pointer-events-none"
                            >
                              {{ col.badge(el) > 99 ? '99+' : col.badge(el) }}
                            </span>
                          </div>
                        </InputComponent>
                      </button>

                      <button
                        @click="col.onClick(el)"
                        v-else-if="typeof col.icon == 'function'"
                        class="ripple p-2.5 rounded relative"
                      >
                        <ColorImg
                          :src="col.icon(el)"
                          class="bg-current w-6 min-w-6 h-6"
                          alt=""
                        />
                        <span
                          v-if="col.badge && col.badge(el) > 0"
                          class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-white shadow-sm pointer-events-none"
                        >
                          {{ col.badge(el) > 99 ? '99+' : col.badge(el) }}
                        </span>
                      </button>
                    </span>
                    <div
                      v-else
                      class="text-gray-700 text-sm shimmerTarget"
                    >
                      <slot :name="'col-' + col.id" :el="el" :col="col">
                        <div v-html="col.value(el)"></div>
                      </slot>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- PAGINATION -->
        <template
          #pagination="{
            page,
            totalPages,
            limit,
            total,
            changePage,
            changeLimit,
            nextPage,
            prevPage,
          }"
        >
          <div v-if="hidePagination" class="hidden"></div>
          <PaginationUi
            v-else
            :page="page"
            :totalPages="totalPages"
            :limit="limit"
            :total="total"
            :changePage="changePage"
            :changeLimit="changeLimit"
            :nextPage="nextPage"
            :prevPage="prevPage"
          />
        </template>
      </ListFetcher>
    </div>
  </div>
</template>
